
import { GoogleGenAI } from "@google/genai";

// Extend window interface for global API key
declare global {
  interface Window {
    GEMINI_API_KEY?: string;
  }
}

export class GeminiService {
  private ai: GoogleGenAI | null = null;
  private requestQueue: Array<() => Promise<void>> = [];
  private isProcessingQueue = false;
  private lastRequestTime = 0;
  private readonly MIN_REQUEST_INTERVAL = 2000;

  constructor() {
    // Only initialize if API key is available
    const apiKey = (typeof process !== 'undefined' && process.env?.API_KEY) || 
                  (typeof window !== 'undefined' && (window as any).GEMINI_API_KEY);
    
    if (apiKey) {
      try {
        this.ai = new GoogleGenAI({ apiKey: apiKey as string });
      } catch (error) {
        console.warn('Failed to initialize Gemini AI:', error);
        this.ai = null;
      }
    } else {
      console.warn('Gemini API key not found. AI features will be disabled.');
      this.ai = null;
    }
  }

  private async enqueue<T>(request: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      this.requestQueue.push(async () => {
        try {
          const now = Date.now();
          const waitTime = Math.max(0, this.lastRequestTime + this.MIN_REQUEST_INTERVAL - now);
          if (waitTime > 0) {
            await new Promise(r => setTimeout(r, waitTime));
          }
          this.lastRequestTime = Date.now();
          const result = await request();
          resolve(result);
        } catch (error: any) {
          reject(error);
        }
      });
      this.processQueue();
    });
  }

  private async processQueue() {
    if (this.isProcessingQueue) return;
    this.isProcessingQueue = true;
    while (this.requestQueue.length > 0) {
      const task = this.requestQueue.shift();
      if (task) await task();
    }
    this.isProcessingQueue = false;
  }

  /**
   * Summarization remains as a detail feature, but searches for posters are removed.
   */
  async getMovieDetails(title: string, year: number) {
    // Return null if AI is not initialized
    if (!this.ai) {
      return {
        summary: `AI features are currently disabled. "${title}" (${year}) is a cinematic masterpiece. Enable AI features by setting up a Gemini API key.`,
        sources: []
      };
    }

    try {
      const response = await this.ai.models.generateContent({
        model: "gemini-3-flash-preview",
        contents: `Summarize the plot and cultural significance of the movie "${title}" (${year}) in exactly two sentences.`,
        config: {
          tools: [{ googleSearch: {} }],
        },
      });

      return {
        summary: response.text,
        sources: response.candidates?.[0]?.groundingMetadata?.groundingChunks?.map(c => c.web?.uri).filter(Boolean).slice(0, 3) || []
      };
    } catch (error) {
      return {
        summary: `Unable to fetch AI details for "${title}" (${year}) at the moment. This film is part of our curated collection.`,
        sources: []
      };
    }
  }
}

export const geminiService = new GeminiService();
